import Qty from 'js-quantities';
import {
    set,
    get,
    convert,
    increase,
    decrease,
    getUnitProfile,
    getUnit,
} from '../helpers/UnitHelpers';
import { isSI } from '../helpers';

import { LOAD_INERTIA_TYPES } from './LoadInertiaAbstractionTypes';

const DYNAMIC_DECIMALS = -1;

class ApplicationData {
    static unitProfiles = [];

    _rtherm_heatsink;
    _rthermWaterCooling;
    _rthermMovingType;
    rthermToolUsed = false;

    _powerSupply;

    listener;

    _powerType = 'dc';

    _loadInertia;

    _loadInertiaSolidHollow = 'solid';

    _loadInertiaMass;

    _loadInertiaRadius;

    _loadInertiaLength;

    _loadInertiaWidth;

    _selectedLoadInertiaAbstraction;

    // All abstractions used to compute the resulting load inertia
    _loadInertiaAbstractions;

    static defaults = [];

    haltChanges = false;
    constructor(scenario = null) {
        this.scenario = scenario;
    }

    set loadInertia(newVal) {
        let massUnit = this.getUnitProfile('mass_system', 'kg');
        let distUnit = this.getUnitProfile('distance_system', 'm');
        distUnit += '2';

        let unit = massUnit + ' ' + distUnit;

        let change = true;
        if (this._loadInertia === undefined) {
            change = false;
        }

        this._loadInertia = set(newVal, unit, null, 5);

        if (change) {
            this.changed();
        }
    }

    get loadInertia() {
        let res = get(this._loadInertia, 5).replace('m2', 'm²');
        if (res === '' || this._loadInertia === undefined) {
            this.loadInertia = this.getDefault('application_data.torque.load_inertia');
        }

        return res;
    }

    increaseLoadInertia() {
        let massUnit = this.getUnitProfile('mass_system', 'kg');
        let distUnit = this.getUnitProfile('distance_system', 'm');
        distUnit += '2';

        let unit = massUnit + ' ' + distUnit;

        if (this.loadInertia === '' || this._loadInertia === undefined) {
            this._loadInertia = new Qty('0 ' + unit);
        }

        this._loadInertia = increase(this._loadInertia);
        this.changed();
    }

    decreaseLoadInertia() {
        let massUnit = this.getUnitProfile('mass_system', 'kg');
        let distUnit = this.getUnitProfile('distance_system', 'm');
        distUnit += '2';

        let unit = massUnit + ' ' + distUnit;

        if (this.loadInertia === '' || this._loadInertia === undefined) {
            this._loadInertia = new Qty('0 ' + unit);
        }

        this._loadInertia = decrease(this._loadInertia);
        this.changed();
    }

    recomputeLoadInertia() {
        const currentAbstraction = this.loadInertiaAbstractions[
            this.selectedLoadInertiaAbstraction
        ];

        if (currentAbstraction.type.hasSolid) {
            if (currentAbstraction.type.hasHollow && this.loadInertiaSolidHollow === 'hollow') {
                this.loadInertiaSolidHollow = 'hollow';
            } else {
                this.loadInertiaSolidHollow = 'solid';
            }
        } else if (currentAbstraction.hasHollow) {
            this.loadInertiaSolidHollow = 'hollow';
        } else {
            this.loadInertiaSolidHollow = undefined;
        }

        // Unfortunetlly getters have side effects in this file so calling all the getters is required
        // to populate the "private" fields with the default values (e.g. _loadInertiaMass)
        currentAbstraction.textualValues = {
            loadInertiaSolidHollow: this.loadInertiaSolidHollow,
            loadInertiaMass: this.loadInertiaMass,
            loadInertiaRadius: this.loadInertiaRadius,
            loadInertiaWidth: this.loadInertiaWidth,
            loadInertiaLength: this.loadInertiaLength,
        };

        currentAbstraction.values = {
            loadInertiaSolidHollow: this._loadInertiaSolidHollow,
            loadInertiaMass: this._loadInertiaMass,
            loadInertiaRadius: this._loadInertiaRadius,
            loadInertiaWidth: this._loadInertiaWidth,
            loadInertiaLength: this._loadInertiaLength,
        };

        let summedLoadInertia = 0;
        this.loadInertiaAbstractions.forEach(abstraction => {
            summedLoadInertia +=
                abstraction.type.formula.length === 3
                    ? abstraction.type.formula(
                          abstraction.values.loadInertiaSolidHollow === 'solid',
                          abstraction.values.loadInertiaMass.scalar,
                          abstraction.values.loadInertiaRadius.scalar,
                      )
                    : abstraction.type.formula(
                          abstraction.values.loadInertiaSolidHollow === 'solid',
                          abstraction.values.loadInertiaMass.scalar,
                          abstraction.values.loadInertiaWidth.scalar,
                          abstraction.values.loadInertiaLength.scalar,
                      );
        });

        this.loadInertia = summedLoadInertia;
    }

    set loadInertiaAbstractions(abstractions) {
        this._loadInertiaAbstractions = abstractions;
    }

    get loadInertiaAbstractions() {
        return this._loadInertiaAbstractions;
    }

    set selectedLoadInertiaAbstraction(abstractionIndex) {
        if (this._selectedLoadInertiaAbstraction === abstractionIndex) return;
        const abstraction = this.loadInertiaAbstractions[abstractionIndex];
        Object.keys(abstraction.values).forEach(vKey => {
            this['_' + vKey] = abstraction.values[vKey];
        });
        this._selectedLoadInertiaAbstraction = abstractionIndex;
    }

    get selectedLoadInertiaAbstraction() {
        return this._selectedLoadInertiaAbstraction;
    }

    set loadInertiaMass(newVal) {
        let unit = this.getUnitProfile('mass_system', 'kg');

        let change = true;
        if (this._loadInertiaMass === undefined) {
            change = false;
        }

        this._loadInertiaMass = set(newVal, unit, null);

        if (change) {
            this.changed();
        }
    }

    get loadInertiaMass() {
        let res = get(this._loadInertiaMass);
        if (res === '' || this._loadInertiaMass === undefined) {
            this.loadInertiaMass = this.getDefault('application_data.torque.load_inertia_mass');
        }

        return res;
    }

    set loadInertiaRadius(newVal) {
        let unit = this.getUnitProfile('distance_system', 'm');

        let change = true;
        if (this._loadInertiaRadius === undefined) {
            change = false;
        }

        this._loadInertiaRadius = set(newVal, unit, null);

        if (change) {
            this.changed();
        }
    }

    get loadInertiaRadius() {
        let res = get(this._loadInertiaRadius);
        if (res === '' || this._loadInertiaRadius === undefined) {
            this.loadInertiaRadius = this.getDefault('application_data.torque.load_inertia_radius');
        }

        return res;
    }

    set loadInertiaLength(newVal) {
        let unit = this.getUnitProfile('distance_system', 'm');

        let change = true;
        if (this._loadInertiaLength === undefined) {
            change = false;
        }

        this._loadInertiaLength = set(newVal, unit, null);

        if (change) {
            this.changed();
        }
    }

    get loadInertiaLength() {
        let res = get(this._loadInertiaLength);
        if (res === '' || this._loadInertiaLength === undefined) {
            this.loadInertiaLength = this.getDefault('application_data.torque.load_inertia_length');
        }

        return res;
    }

    set loadInertiaWidth(newVal) {
        let unit = this.getUnitProfile('distance_system', 'm');

        let change = true;
        if (this._loadInertiaWidth === undefined) {
            change = false;
        }

        this._loadInertiaWidth = set(newVal, unit, null);

        if (change) {
            this.changed();
        }
    }

    get loadInertiaWidth() {
        let res = get(this._loadInertiaWidth);
        if (res === '' || this._loadInertiaWidth === undefined) {
            this.loadInertiaWidth = this.getDefault('application_data.torque.load_inertia_width');
        }

        return res;
    }

    /* thermal resistance heatsink */

    set rthermSurfaceArea(newVal) {
        const unit = this.getUnitProfile('area_short', 'm^2');
        const change = this._rthermSurfaceArea !== undefined;
        this._rthermSurfaceArea = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get rthermSurfaceArea() {
        if (this._rthermSurfaceArea === undefined || get(this._rthermSurfaceArea) === '') {
            if (this._rtherm_tool_saved && this._rtherm_tool_saved.rthermSurfaceArea) {
                this.rthermSurfaceArea = this._rtherm_tool_saved.rthermSurfaceArea;
            } else {
                this.rthermSurfaceArea = this.getDefault('application_data.rtherm_surface_area');
            }
        }
        return get(this._rthermSurfaceArea).replace('m2', 'm²');
    }

    set rthermMotorLength(newVal) {
        const unit = this.getUnitProfile('distance_short', 'm');
        const change = this._rthermMotorLength !== undefined;
        this._rthermMotorLength = set(newVal, unit);

        if (change) {
            this.changed();
        }
    }

    get rthermMotorLength() {
        if (this._rthermMotorLength === undefined || get(this._rthermMotorLength) === '') {
            this.rthermMotorLength =
                this._rtherm_tool_saved && this._rtherm_tool_saved.rthermMotorLength
                    ? this._rtherm_tool_saved.rthermMotorLength
                    : 0.2;
        }
        return get(this._rthermMotorLength);
    }

    set rthermMotorWidth(newVal) {
        const unit = this.getUnitProfile('distance_short', 'm');
        const change = this._rthermMotorWidth !== undefined;
        this._rthermMotorWidth = set(newVal, unit);

        if (change) {
            this.changed();
        }
    }

    get rthermMotorWidth() {
        if (this._rthermMotorWidth === undefined || get(this._rthermMotorWidth) === '') {
            this.rthermMotorWidth =
                this._rtherm_tool_saved && this._rtherm_tool_saved.rthermMotorWidth
                    ? this._rtherm_tool_saved.rthermMotorWidth
                    : 0.2;
        }
        return get(this._rthermMotorWidth);
    }

    set rthermFlow(newVal) {
        const unit = this.getUnitProfile('flow', 'l/min');
        const change = this._rthermFlow !== undefined;
        this._rthermFlow = set(newVal, unit);

        if (change) {
            this.changed();
        }
    }

    get rthermFlow() {
        if (this._rthermFlow === undefined || get(this._rthermFlow) === '') {
            if (this._rtherm_tool_saved && this._rtherm_tool_saved.rthermFlow) {
                this.rthermFlow = this._rtherm_tool_saved.rthermFlow;
            } else {
                this.rthermFlow = this.getDefault('application_data.rtherm_flow');
            }
        }
        return get(this._rthermFlow);
    }

    recomputeRthermHeatsink() {
        if (!this.rthermToolUsed) return;

        let unitConversion = !isSI(this.units);
        if (unitConversion) {
            this.haltChanges = true;
            this.convert('si');
        }

        const H_AIR_BASE = 10; // W/m^2K
        const H_AIR_VELOCITY = 4; // W/m^2K
        const H_WATER_BASE = 580; // W/M^2K
        const H_WATER_VELOCITY = 2100; // W/M^2K
        const CHANNEL_DIAMETER = 4; // mm
        const COOLING_CHANNELS = 2;
        const COOLING_CHANNELS_QTL = 4;

        const motorLength = Qty(this.rthermMotorLength).scalar;

        let coolingSurface;
        let h;
        if (this.scenario.isTorque()) {
            const motorWidth = Qty(this.rthermMotorWidth).scalar;
            const surfaceArea = Math.max(
                this._rthermSurfaceArea.scalar,
                Math.PI * motorLength * motorWidth,
            );
            if (this._rthermWaterCooling === 'yes') {
                const flow = this._rthermFlow.scalar;
                const waterVelocity = (100 * flow) / (6 * Math.PI * CHANNEL_DIAMETER);
                h = H_WATER_BASE + H_WATER_VELOCITY * Math.sqrt(waterVelocity);
                const coolingChannels = this.scenario.motor.name.toLowerCase().includes('qtl')
                    ? COOLING_CHANNELS_QTL
                    : COOLING_CHANNELS;
                coolingSurface =
                    (motorWidth * coolingChannels * Math.PI ** 2 * CHANNEL_DIAMETER) / 1000;
            } else {
                h = H_AIR_BASE;
                coolingSurface = surfaceArea;
            }
        } else {
            if (this._rthermWaterCooling === 'yes') {
                const flow = Qty(this.rthermFlow).scalar;
                const waterVelocity = (100 * flow) / (6 * Math.PI * CHANNEL_DIAMETER);
                coolingSurface =
                    (motorLength * COOLING_CHANNELS * CHANNEL_DIAMETER * Math.PI) / 1000;
                h = H_WATER_BASE + H_WATER_VELOCITY * Math.sqrt(waterVelocity);
            } else {
                const motorWidth = Qty(this.rthermMotorWidth).scalar;
                coolingSurface = Math.max(this._rthermSurfaceArea.scalar, motorLength * motorWidth);
                if (this._rthermMovingType === 'moving_coil') {
                    const rms = this.calculateRMS();
                    h = H_AIR_BASE + H_AIR_VELOCITY * rms;
                } else {
                    h = H_AIR_BASE;
                }
            }
        }
        const Rth = 1 / (coolingSurface * h);
        this._rtherm_heatsink = set(Rth, 'degK/W');

        if (unitConversion) {
            this.convert('imperial');
            this.haltChanges = false;
        }
    }

    calculateRMS() {
        let rms = 1;
        if (this.scenario.results && this.scenario.results.vrms !== '') {
            const value = this.scenario.results.vrms.value;
            rms = parseFloat(value.split(' ')[0]);
        }
        return rms;
    }

    set powerType(newVal) {
        let change = true;
        if (this._powerType === undefined) {
            change = false;
        }

        if (newVal === 'ac' || newVal === 'dc') {
            this._powerType = newVal;
        } else {
            this._powerType = 'dc';
        }

        if (change) {
            this.changed();
        }
    }

    get powerType() {
        return this._powerType;
    }

    set loadInertiaSolidHollow(newVal) {
        this._loadInertiaSolidHollow = newVal;
    }

    get loadInertiaSolidHollow() {
        return this._loadInertiaSolidHollow;
    }

    set tempHeatsink(newVal) {
        if (newVal === undefined) {
            this._tempHeatsink = undefined;
            return;
        }

        let change = true;
        if (this._tempHeatsink === undefined) {
            change = false;
        }

        if (!isNaN(newVal)) {
            let unit = this.getUnitProfile('temperature_system', 'degC');

            if (!unit) {
                unit = 'tempC';
            } else {
                if (unit === 'degF') {
                    unit = 'tempF';
                } else if (unit === 'degC') {
                    unit = 'tempC';
                } else {
                    unit = 'tempK';
                }
            }

            newVal += unit;
        }

        if (
            newVal.indexOf('tempC') === -1 &&
            newVal.indexOf('tempF') === -1 &&
            newVal.indexOf('tempK') === -1
        ) {
            newVal = newVal
                .replace('°C', 'tempC')
                .replace('°c', 'tempC')
                .replace('°F', 'tempF')
                .replace('°f', 'tempF')
                .replace('k', 'tempK')
                .replace('K', 'tempK');
        }

        let qty = Qty.parse(newVal.replace(',', '.'));

        if (qty !== null && qty.isCompatible(new Qty('1 tempC'))) {
            qty.scalar = parseFloat(Number(qty.scalar).toFixed(2));
            this._tempHeatsink = qty;
        } else {
            this._tempHeatsink = undefined;
        }

        if (change) {
            this.changed();
        }
    }

    get tempHeatsink() {
        if (this._tempHeatsink === undefined || this._tempHeatsink === null) {
            if (this.scenario !== null && this.scenario.isTorque()) {
                this.tempHeatsink = this.getDefault('application_data.torque.temp_heatsink');
            } else {
                this.tempHeatsink = this.getDefault('application_data.linear.temp_heatsink');
            }
        }

        return this._tempHeatsink
            .toString()
            .replace('tempC', '°C')
            .replace('tempF', '°F')
            .replace('tempK', 'K');
    }

    set rtherm_heatsink(newVal) {
        if (newVal === undefined) {
            this._rtherm_heatsink = undefined;
            return;
        }

        let change = true;
        if (this._rtherm_heatsink === undefined) {
            change = false;
        }

        if (!isNaN(newVal)) {
            let tempUnit = this.getUnitProfile('temperature', 'kelvin');
            let powerUnit = this.getUnitProfile('power', 'W');
            let unit;

            if (!tempUnit && !powerUnit) {
                unit = 'degK/W';
            } else {
                if (!tempUnit) {
                    tempUnit = 'kelvin';
                }

                if (!powerUnit || powerUnit === undefined) {
                    powerUnit = 'W';
                }

                if (tempUnit === 'fahrenheit') {
                    unit = 'degF/' + powerUnit;
                } else if (tempUnit === 'celsius') {
                    unit = 'degC/' + powerUnit;
                } else {
                    unit = 'degK/' + powerUnit;
                }
            }

            newVal += unit;
        }

        if (
            newVal.indexOf('degC') === -1 &&
            newVal.indexOf('degF') === -1 &&
            newVal.indexOf('degK') === -1
        ) {
            newVal = newVal
                .replace('°C', 'degC')
                .replace('°c', 'degC')
                .replace('°F', 'degF')
                .replace('°f', 'degF')
                .replace('k', 'degK')
                .replace('K', 'degK');
        }

        let qty = Qty.parse(newVal.replace(',', '.'));

        if (qty !== null && qty.isCompatible(new Qty('1 degC/W'))) {
            qty.scalar = parseFloat(Number(qty.scalar).toFixed(3));
            this._rtherm_heatsink = qty;
        } else {
            this._rtherm_heatsink = undefined;
        }

        if (change) {
            this.changed();
        }
    }

    get rtherm_heatsink() {
        if (this._rtherm_heatsink === undefined || this._rtherm_heatsink === null) {
            this.rtherm_heatsink = this.getDefault('application_data.linear.rtherm_heatsink');
            this.recomputeRthermHeatsink();
        }

        return this._rtherm_heatsink
            .toString()
            .replace('degC', '°C')
            .replace('degF', '°F')
            .replace('degK', 'K');
    }

    /**
     * Moving Mass. If no unit is passed, kg will be used as default.
     * @param newVal
     */
    set movingMass(newVal) {
        let unit = this.getUnitProfile('mass_system', 'kg');
        let change = true;
        if (this._movingMass === undefined) {
            change = false;
        }

        this._movingMass = set(newVal, unit);

        if (change) {
            this.changed();
        }
    }

    get movingMass() {
        let res = get(this._movingMass);
        if (res === '') {
            //Get default
            this.movingMass = this.getDefault('application_data.linear.moving_mass');
        }

        return res;
    }

    increaseMovingMass() {
        let massUnit = this.getUnitProfile('mass_system', 'kg');

        if (this.movingMass === '' || this._movingMass === undefined) {
            this._movingMass = new Qty('0 ' + massUnit);
        }

        this._movingMass = increase(this._movingMass);
        this.changed();
    }

    decreaseMovingMass() {
        let massUnit = this.getUnitProfile('mass_system', 'kg');

        if (this.movingMass === '' || this._movingMass === undefined) {
            this._movingMass = new Qty('0 ' + massUnit);
        }

        this._movingMass = decrease(this._movingMass);
        this.changed();
    }

    set friction(newVal) {
        let change = true;
        if (this._friction === undefined) {
            change = false;
        }

        let unit = this.getUnitProfile('torque_system', 'N*m');

        this._friction = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get friction() {
        let res = get(this._friction, DYNAMIC_DECIMALS);
        if (res === '') {
            this.friction = this.getDefault('application_data.torque.friction');
        }

        return res;
    }

    increaseFriction() {
        let unit = this.getUnitProfile('torque_system', 'N*m');

        if (this.friction === '' || this._friction === undefined) {
            this._friction = new Qty('0 ' + unit);
        }

        this._friction = increase(this._friction);
        this.changed();
    }

    decreaseFriction() {
        let unit = this.getUnitProfile('torque_system', 'N*m');

        if (this.friction === '' || this._friction === undefined) {
            this._friction = new Qty('0 ' + unit);
        }

        this._friction = decrease(this._friction);
        this.changed();
    }

    set inclinementAngle(newVal) {
        let change = true;
        if (this._inclinementAngle === undefined) {
            change = false;
        }

        this._inclinementAngle = set(newVal, 'degree');
        if (change) {
            this.changed();
        }
    }

    get inclinementAngle() {
        let res = get(this._inclinementAngle, DYNAMIC_DECIMALS);
        if (res === '') {
            //Get default
            this.inclinementAngle = this.getDefault('application_data.linear.inclinement_angle');
        }

        return res;
    }

    set frictionForce(newVal) {
        let unit = this.getUnitProfile('force_system', 'N');
        if (!unit) {
            unit = 'N';
        }

        let change = true;
        if (this._frictionForce === undefined) {
            change = false;
        }

        this._frictionForce = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    get frictionForce() {
        let res = get(this._frictionForce, DYNAMIC_DECIMALS);
        if (res === '') {
            //Get default
            this.frictionForce = this.getDefault('application_data.linear.friction_force');
        }

        return res;
    }

    increaseFrictionForce() {
        let unit = this.getUnitProfile('force_system', 'N');
        if (!unit) {
            unit = 'N';
        }

        if (this.frictionForce === '' || this._frictionForce === undefined) {
            this._frictionForce = new Qty('0 ' + unit);
        }

        this._frictionForce = increase(this._frictionForce);
        this.changed();
    }

    decreaseFrictionForce() {
        let unit = this.getUnitProfile('force_system', 'N');
        if (!unit) {
            unit = 'N';
        }

        if (this.frictionForce === '' || this._frictionForce === undefined) {
            this._frictionForce = new Qty('0 ' + unit);
        }

        this._frictionForce = decrease(this._frictionForce);
        this.changed();
    }

    /**
     * Power supply for the quick edit field. Shows AC/DC behind the value.
     */
    get quickEditPowerSupply() {
        return this.powerSupply + ' ' + this.powerType.toUpperCase();
    }

    set quickEditPowerSupply(newVal) {
        let options = ['DC', 'AC'];
        if (newVal !== undefined && newVal !== null) {
            //400 V AC
            if (newVal.toLowerCase().indexOf('dc') >= 0) {
                this.powerType = 'dc';
                this.powerSupply = newVal.replace('dc', '');
            } else if (newVal.toLowerCase().indexOf('ac') >= 0) {
                this.powerType = 'ac';
                this.powerSupply = newVal.replace('ac', '');
            } else {
                this.powerSupply = newVal;
            }
        }
    }

    get powerSupply() {
        let res = get(this._powerSupply, DYNAMIC_DECIMALS);
        if (res === '') {
            if (this.scenario !== null && this.scenario.isTorque()) {
                //Get default
                this.powerSupply = this.getDefault('application_data.torque.power_supply');
            } else {
                this.powerSupply = this.getDefault('application_data.linear.power_supply');
            }
        }

        return res;
    }

    set powerSupply(newVal) {
        let change = true;
        if (this._powerSupply === undefined) {
            change = false;
        }
        let unit = this.getUnitProfile('voltage_system', 'V');
        this._powerSupply = set(newVal, unit);
        if (change) {
            this.changed();
        }
    }

    increasePowerSupply() {
        let unit = this.getUnitProfile('voltage_system', 'V');

        if (this.powerSupply === '' || this._powerSupply === undefined) {
            this._powerSupply = new Qty('0 ' + unit);
        }

        this._powerSupply = increase(this._powerSupply);
        this.changed();
    }

    decreasePowerSupply() {
        let unit = this.getUnitProfile('voltage_system', 'V');

        if (this.powerSupply === '' || this._powerSupply === undefined) {
            this._powerSupply = new Qty('0 ' + unit);
        }

        this._powerSupply = decrease(this._powerSupply);
        this.changed();
    }

    get unit_profile() {
        return ApplicationData.unitProfile;
    }

    listen(callback) {
        this.listener = callback;
    }

    changed() {
        if (this.listener !== undefined && !this.haltChanges) {
            this.listener('App Data');
        }
    }

    /**
     * Convert all profile values to either SI or US units
     * @param unitSystem
     */
    convert(unitSystem = 'si') {
        this.units = getUnitProfile(unitSystem);

        ApplicationData.unitProfile = ApplicationData.unitProfiles.find(item => {
            return item.name.toLowerCase() === unitSystem.toLowerCase();
        });

        if (unitSystem.toLowerCase().indexOf('si') < 0) {
            this.frictionForce = convert(
                this._frictionForce,
                this.getUnitProfile('force_system', 'lbf'),
            );

            this.movingMass = convert(this._movingMass, this.getUnitProfile('mass_system', 'lbs'));
            this.tempHeatsink = convert(this._tempHeatsink, 'tempF');
            this.rtherm_heatsink = convert(this._rtherm_heatsink, 'degF/hp');
            this.friction = convert(this._friction, this.getTorqueForceUnit('lbf', 'yd'));
            this.loadInertia = convert(this._loadInertia, this.getInertiaUnit('lbs', 'yd'));
            this.loadInertiaMass = convert(
                this._loadInertiaMass,
                this.getUnitProfile('mass_system', 'lbs'),
            );
            this.loadInertiaRadius = convert(
                this._loadInertiaRadius,
                this.getUnitProfile('distance_system', 'yd'),
            );
            this.loadInertiaLength = convert(
                this._loadInertiaLength,
                this.getUnitProfile('distance_system', 'yd'),
            );
            this.loadInertiaWidth = convert(
                this._loadInertiaWidth,
                this.getUnitProfile('distance_system', 'yd'),
            );
            this.rthermSurfaceArea = convert(
                this._rthermSurfaceArea,
                this.getUnitProfile('area_short', 'yd^2'),
            );
            this.rthermMotorLength = convert(
                this._rthermMotorLength,
                this.getUnitProfile('distance_system', 'yd'),
            );
            this.rthermMotorWidth = convert(
                this._rthermMotorWidth,
                this.getUnitProfile('distance_system', 'yd'),
            );
            this.rthermFlow = convert(this._rthermFlow, this.getUnitProfile('flow', 'gal/min'));
        } else {
            this.movingMass = convert(this._movingMass, this.getUnitProfile('mass', 'kg'));
            this.frictionForce = convert(this._frictionForce, this.getUnitProfile('force', 'N'));

            this.tempHeatsink = convert(this._tempHeatsink, 'tempC');
            this.rtherm_heatsink = convert(this._rtherm_heatsink, 'degK/W');
            this.friction = convert(this._friction, this.getTorqueForceUnit('N', 'm'));
            this.loadInertia = convert(this._loadInertia, this.getInertiaUnit('kg', 'm'));
            this.loadInertiaMass = convert(
                this._loadInertiaMass,
                this.getUnitProfile('mass_system', 'kg'),
            );
            this.loadInertiaRadius = convert(
                this._loadInertiaRadius,
                this.getUnitProfile('distance_system', 'm'),
            );
            this.loadInertiaLength = convert(
                this._loadInertiaLength,
                this.getUnitProfile('distance_system', 'm'),
            );
            this.loadInertiaWidth = convert(
                this._loadInertiaWidth,
                this.getUnitProfile('distance_system', 'm'),
            );
            this.rthermSurfaceArea = convert(
                this._rthermSurfaceArea,
                this.getUnitProfile('area_short', 'm^2'),
            );
            this.rthermMotorLength = convert(
                this._rthermMotorLength,
                this.getUnitProfile('distance_system', 'm'),
            );
            this.rthermMotorWidth = convert(
                this._rthermMotorWidth,
                this.getUnitProfile('distance_system', 'm'),
            );
            this.rthermFlow = convert(this._rthermFlow, this.getUnitProfile('flow', 'l/min'));
        }
    }

    getInertiaUnit(weight, dist) {
        let massUnit = this.getUnitProfile('mass_system', weight);
        let distUnit = this.getUnitProfile('distance_system', dist);

        return massUnit + ' ' + distUnit + '2';
    }

    getTorqueForceUnit(force, dist) {
        return this.getUnitProfile('torque_system', 'N*m');
    }

    isEmpty() {
        return (
            this.tempHeatsink === '' &&
            this.movingMass === '' &&
            this.inclinementAngle === '' &&
            this.frictionForce === '' &&
            this.rtherm_heatsink === undefined &&
            this.powerSupply === ''
        );
    }

    /**
     * Create a deep copy of the object.
     * @returns {*}
     */
    copy() {
        let copy = new ApplicationData();
        copy.tempHeatsink = this.tempHeatsink;
        copy.movingMass = this.movingMass;
        copy.frictionForce = this.frictionForce;
        copy.friction = this.friction;
        copy.rtherm_heatsink = this.rtherm_heatsink;
        copy._rthermWaterCooling = this._rthermWaterCooling;
        copy._rthermMovingType = this._rthermMovingType;
        copy.rthermSurfaceArea = this.rthermSurfaceArea;
        copy.rthermMotorLength = this.rthermMotorLength;
        copy.rthermMotorWidth = this.rthermMotorWidth;
        copy.rthermToolUsed = this.rthermToolUsed;
        copy.rthermFlow = this.rthermFlow;
        copy.inclinementAngle = this.inclinementAngle;
        copy.powerSupply = this.powerSupply;
        copy.powerType = this.powerType;
        copy.loadInertia = this.loadInertia;
        copy.loadInertiaAbstractions = this.loadInertiaAbstractions;
        copy.selectedLoadInertiaAbstraction = this.selectedLoadInertiaAbstraction;

        return copy;
    }

    getDefault(key) {
        if (!ApplicationData.defaults || ApplicationData.defaults.length === 0) {
            ApplicationData.defaults = JSON.parse(localStorage.getItem('settings'));
        }

        if (ApplicationData.defaults) {
            let setting = ApplicationData.defaults.find(item => item.key === key);
            if (setting !== undefined) {
                return setting.value;
            }
        }

        return 0;
    }

    getUnitProfile(key, fallback) {
        return getUnit(this.units, key, fallback);
    }

    /**
     * Create a json representation of the movement profile.
     * @returns {{moving_distance, moving_time, acc_time, max_volts, load, order}}
     */
    toJson(torque = false) {
        let cpy = this.copy();
        cpy.convert('si');

        return {
            temp_heatsink: cpy._tempHeatsink.scalar,
            rtherm_heatsink: cpy._rtherm_heatsink.scalar,
            moving_mass: torque
                ? Qty.parse(cpy.loadInertia.replace('m²', 'm2')).scalar
                : Qty.parse(cpy.movingMass).scalar,
            inclinement_angle: Qty.parse(cpy.inclinementAngle).scalar,
            friction_force: torque
                ? Qty.parse(cpy.friction).scalar
                : Qty.parse(cpy.frictionForce).scalar,
            voltage: ApplicationData.convertPower(
                Qty.parse(cpy.powerSupply).scalar,
                this.powerType,
                'dc',
            ),
            dc: this.powerType === 'dc',
            frontend_meta: JSON.stringify({
                load_inertia_tool: {
                    abstractions: cpy.loadInertiaAbstractions,
                    selected_abstraction: cpy.selectedLoadInertiaAbstraction,
                },
                rtherm_tool: cpy.rthermToolUsed
                    ? {
                          rthermWaterCooling: cpy._rthermWaterCooling,
                          rthermMovingType: cpy._rthermMovingType,
                          rthermSurfaceArea: cpy._rthermSurfaceArea.scalar,
                          rthermFlow: cpy._rthermFlow.scalar,
                      }
                    : {},
            }),
        };
    }

    static convertPower(power, from = 'dc', to = 'dc') {
        if (to === from) {
            return power;
        }

        if (from === 'ac' && to === 'dc') {
            return power * Math.sqrt(2);
        }

        if (from === 'dc' && to === 'ac') {
            return power / Math.sqrt(2);
        }

        return power;
    }

    /**
     * Create an instance form a json object.
     * @param json
     * @param dc
     * @param torque
     * @returns {MovementProfile}
     */
    static fromJson(json, dc = true, torque = false, scenario = null) {
        let app = new ApplicationData(scenario);

        if (json !== undefined) {
            app.tempHeatsink = json.temp_heatsink;
            app.rtherm_heatsink = json.rtherm_heatsink;

            if (torque) {
                app.loadInertia = json.moving_mass;
                app.friction = json.friction_force;
            } else {
                app.movingMass = json.moving_mass;
                app.frictionForce = json.friction_force;
            }

            app.inclinementAngle = json.inclinement_angle;
            app.powerType = dc;
            app.powerSupply = ApplicationData.convertPower(json.voltage, 'dc', app.powerType);

            if (json.frontend_meta) {
                const frontendMeta = JSON.parse(json.frontend_meta);
                if (frontendMeta.load_inertia_tool && frontendMeta.load_inertia_tool.abstractions) {
                    const abstractions = frontendMeta.load_inertia_tool.abstractions.map(
                        abstraction => {
                            return {
                                type: LOAD_INERTIA_TYPES.find(
                                    type => type.id === abstraction.type.id,
                                ),
                                values: {
                                    loadInertiaMass: Qty(abstraction.values.loadInertiaMass),
                                    loadInertiaRadius: Qty(abstraction.values.loadInertiaRadius),
                                    loadInertiaLength: Qty(abstraction.values.loadInertiaLength),
                                    loadInertiaWidth: Qty(abstraction.values.loadInertiaWidth),
                                    loadInertiaSolidHollow:
                                        abstraction.values.loadInertiaSolidHollow,
                                },
                                textualValues: abstraction.textualValues,
                            };
                        },
                    );
                    app.loadInertiaAbstractions = abstractions;
                    app.selectedLoadInertiaAbstraction = frontendMeta.selected_abstraction;
                }
                if (frontendMeta.rtherm_tool && Object.keys(frontendMeta.rtherm_tool).length) {
                    app._rtherm_tool_saved = frontendMeta.rtherm_tool;
                    app._rthermWaterCooling = frontendMeta.rtherm_tool.rthermWaterCooling;
                    app._rthermMovingType = frontendMeta.rtherm_tool.rthermMovingType;
                    app.rthermToolUsed = true;
                }
            }
        }

        return app;
    }
}

export default ApplicationData;
